import {
  IconButton,
  Dialog,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useEffect } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import { styled } from "@mui/material/styles";
import preAuth from "../../gql/preAuth";
import { useMutation, useQuery } from "@apollo/client";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppButton from "../../shared/AppButton";
import AppTable from "../../shared/AppTable";
import CustomFilterInput from "../../shared/CustomFilterInput";
import { CheckIcon } from "@heroicons/react/20/solid";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(1.5),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1.5),
  },
}));

export default function ConvertPreAuthToClaimModal({
  showConvertToClaimModal,
  setShowConvertToClaimModal,
  selectedItem,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    loading: loadingDetails,
    data: detailsData,
  } = useQuery(preAuth.GET_PREAUTH_TRANSACTION_DETAILS, {
    variables: {
      transactionId: selectedItem?.transaction_id,
    },
    skip: !selectedItem?.transaction_id || !showConvertToClaimModal,
  });

  const [convertPreAuthToClaim, { loading }] = useMutation(preAuth.PRE_AUTH_TO_CLAIM, {
    update(proxy, result) {
      onSuccessfulCreateRequestResponse(result.data);
    },
    onError(error) {
      onCreateResponseError(error.graphQLErrors[0]);
    },
  });

  const onSuccessfulCreateRequestResponse = (data) => {
    if (data?.preAuthToClaim) {
      toast.success(data?.preAuthToClaim?.message);
      formik.resetForm();
      setShowConvertToClaimModal(false);
      navigate("/claims/submit");
    } else {
    }
  };

  const onCreateResponseError = (error) => {
    toast.error(`${error?.message || "Error"}`);
  };

  const formik = useFormik({
    initialValues: {
      episode_no: "",
      transaction_id: "",
      items: [],
    },

    validationSchema: () => {
      return Yup.object().shape({
        episode_no: Yup.string().required(t("Episode No required")),
        transaction_id: Yup.string().required(t("Pre Auth Id required")),
        items: Yup.array().of(
          Yup.object().shape({
            invoice_no: Yup.string().required(t("Invoice No required")),
            patient_share: Yup.number().required(t("Patient Share required")),
            payer_share: Yup.number().required(t("Payer Share required")),
            payer_share_with_tax: Yup.number().required(
              t("Payer Share With Tax required"),
            ),
            discount: Yup.number().required(t("Discount required")),
          }),
        ),
      });
    },

    onSubmit: (values) => {
      // MODIFIED: Also remove `status` and `name` from the payload before submission
      const newItems = values?.items.map(({ name, status, ...rest }) => rest);
      const rest = {
        ...values,
        items: newItems,
      };
      convertPreAuthToClaim({
        variables: {
          input: rest,
        },
      });
    },
  });

  // NEW: Handler to delete an item from the formik state
  const handleDeleteItem = (indexToDelete) => {
    // Get a copy of the current items array
    const currentItems = [...formik.values.items];
    // Filter out the item at the specified index
    const updatedItems = currentItems.filter(
      (_, index) => index !== indexToDelete,
    );
    // Update the formik state with the new array
    formik.setFieldValue("items", updatedItems);
  };

  useEffect(() => {
    if (selectedItem) {
      formik.setFieldValue("transaction_id", selectedItem?.transaction_id);
    }
  }, [selectedItem, showConvertToClaimModal]);

  useEffect(() => {
    if (detailsData && detailsData.getPreAuthTransaction) {
      const extractedItems =
        detailsData?.getPreAuthTransaction?.data?.items?.map((item) => ({
          id: item.id,
          patient_share: item?.patient_share || 0,
          invoice_no: "",
          payer_share: item?.payer_share || 0,
          payer_share_with_tax: item?.tax_amount + item?.payer_share || 0,
          discount: item?.discount_amount || 0,
          name: item?.standard_description,
          // MODIFIED: Added status from the fetched data
          status: item?.status || "N/A", // Provide a fallback
        }));

      formik.setValues({
        ...formik.values,
        items: extractedItems || [], // Ensure items is always an array
      });
    }
  }, [detailsData]);

  const itemColumns = [
    {
      header: t("Name"),
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-700">
          {row?.name || "___"}
        </span>
      ),
    },
    {
      header: t("Status"),
      render: (row) => (
        <span className="text-[12px] font-semibold text-slate-700">
          {row?.status || "___"}
        </span>
      ),
    },
    {
      header: t("Invoice No"),
      render: (row, rowIndex) => {
        const name = `items[${rowIndex}].invoice_no`;
        const error = formik.errors?.items?.[rowIndex]?.invoice_no;
        const touched = formik.touched?.items?.[rowIndex]?.invoice_no;
        return (
          <CustomFilterInput
            label=""
            name={name}
            placeholder={t("Invoice No")}
            value={formik.values?.items?.[rowIndex]?.invoice_no ?? ""}
            onChange={(e) => formik.setFieldValue(name, e.target.value)}
            onBlur={formik.handleBlur}
            error={error}
            touched={touched}
            hideErrorText
          />
        );
      },
    },
    {
      header: t("Patient Share"),
      render: (row, rowIndex) => {
        const name = `items[${rowIndex}].patient_share`;
        const error = formik.errors?.items?.[rowIndex]?.patient_share;
        const touched = formik.touched?.items?.[rowIndex]?.patient_share;
        return (
          <CustomFilterInput
            label=""
            name={name}
            type="number"
            placeholder={t("Patient Share")}
            value={formik.values?.items?.[rowIndex]?.patient_share ?? 0}
            onChange={(e) => formik.setFieldValue(name, e.target.value)}
            onBlur={formik.handleBlur}
            error={error}
            touched={touched}
            hideErrorText
          />
        );
      },
    },
    {
      header: t("Payer Share"),
      render: (row, rowIndex) => {
        const name = `items[${rowIndex}].payer_share`;
        const error = formik.errors?.items?.[rowIndex]?.payer_share;
        const touched = formik.touched?.items?.[rowIndex]?.payer_share;
        return (
          <CustomFilterInput
            label=""
            name={name}
            type="number"
            placeholder={t("Payer Share")}
            value={formik.values?.items?.[rowIndex]?.payer_share ?? 0}
            onChange={(e) => formik.setFieldValue(name, e.target.value)}
            onBlur={formik.handleBlur}
            error={error}
            touched={touched}
            hideErrorText
          />
        );
      },
    },
    {
      header: t("Payer Share With Tax"),
      render: (row, rowIndex) => {
        const name = `items[${rowIndex}].payer_share_with_tax`;
        const error = formik.errors?.items?.[rowIndex]?.payer_share_with_tax;
        const touched = formik.touched?.items?.[rowIndex]?.payer_share_with_tax;
        return (
          <CustomFilterInput
            label=""
            name={name}
            type="number"
            placeholder={t("Payer Share With Tax")}
            value={formik.values?.items?.[rowIndex]?.payer_share_with_tax ?? 0}
            onChange={(e) => formik.setFieldValue(name, e.target.value)}
            onBlur={formik.handleBlur}
            error={error}
            touched={touched}
            hideErrorText
          />
        );
      },
    },
    {
      header: t("Discount"),
      render: (row, rowIndex) => {
        const name = `items[${rowIndex}].discount`;
        const error = formik.errors?.items?.[rowIndex]?.discount;
        const touched = formik.touched?.items?.[rowIndex]?.discount;
        return (
          <CustomFilterInput
            label=""
            name={name}
            type="number"
            placeholder={t("Discount")}
            value={formik.values?.items?.[rowIndex]?.discount ?? 0}
            onChange={(e) => formik.setFieldValue(name, e.target.value)}
            onBlur={formik.handleBlur}
            error={error}
            touched={touched}
            hideErrorText
          />
        );
      },
    },
    {
      header: t("Actions"),
      render: (row, rowIndex) => (
        <div className="flex justify-center">
          <Tooltip title={t("Delete Item")}>
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDeleteItem(rowIndex)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
      align: "center",
    },
  ];

  return (
    <div>
      <BootstrapDialog
        onClose={() => setShowConvertToClaimModal(false)}
        aria-labelledby="customized-dialog-title"
        open={showConvertToClaimModal}
        PaperProps={{
          className:
            "w-full !max-w-[980px] sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-2xl",
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: 1.5, fontSize: "0.95rem", fontWeight: 700 }}
          id="customized-dialog-title"
        >
          {t("Convert PreAuth To Claim")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setShowConvertToClaimModal(false)}
          className={`${i18n.language === "ar" ? "left-2" : "right-2"} `}
          sx={{
            position: "absolute",
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <div className="benefeciary-create">
            {/* Episode No. Field */}
            <div className="personal-data flex flex-col gap-2 !shadow-none !border-none !p-0">
              <div className="personal-fields grid grid-cols-1 lg:grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                  <CustomFilterInput
                    label={t("Episode No.")}
                    name="episode_no"
                    placeholder={t("Episode No.")}
                    value={formik.values?.episode_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors.episode_no}
                    touched={formik.touched.episode_no}
                    className="episode_no"
                  />
                </div>
              </div>
            </div>

            <input
              type="hidden"
              name="transaction_id"
              value={formik.values.transaction_id}
            />
            {formik.errors.transaction_id && (
              <p className="text-sm text-red-500">
                {formik.errors.transaction_id}
              </p>
            )}

            <div className="flex items-center justify-between mt-3 mb-2">
              <div className="text-[13px] font-bold text-slate-800">
                {t("Items")}
              </div>
              <div className="text-[12px] text-slate-500">
                {t("Total")}: {formik.values?.items?.length ?? 0}
              </div>
            </div>

            <AppTable
              columns={itemColumns}
              data={formik.values?.items || []}
              loading={loadingDetails}
              actions={false}
            />
          </div>
        </DialogContent>

        <DialogActions>
          <div className="w-full flex justify-end gap-2">
            <AppButton
              label={t("Cancel")}
              variant="outlined"
              bgColor="#6B7280"
              onClick={() => setShowConvertToClaimModal(false)}
              disabled={loading || loadingDetails}
            />
            <AppButton
              label={t("Claim Ready For Submission")}
              startIcon={<CheckIcon className="w-4 h-4" />}
              onClick={() => formik.handleSubmit()}
              isLoading={loading}
              loadingText={t("Submitting...")}
              disabled={loadingDetails || loading}
            />
          </div>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
}
